import { useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import {
  ReactiveHireQueryKeys,
  getReactiveHireRequests,
} from "@/api/reactiveHire/reactiveHire.queries";
import { updateReactiveHireRequestStatus } from "@/api/reactiveHire/reactiveHire.mutations";
import type {
  IReactiveHireRequest,
  ReactiveHireRequestStatus,
} from "@/api/reactiveHire/reactiveHire.types";
import { REACTIVE_HIRE_STATUS_TRANSITIONS } from "@/api/reactiveHire/reactiveHire.types";
import { getActiveSessionState } from "@/hooks/useActiveSession";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { useCrossTenantForSuperAdmin, useLeaveWhenInsideEntity } from "@/hooks/usePlatformView";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { formatIsoDateToDisplayDateTime } from "@/utils/dateFormatter";
import { getRouteIcon, routeIconKeys } from "@/utils/getRouteIconComponent";
import { isReactiveHireRep } from "@/utils/reactiveHire/isReactiveHireRep";
import { handleApiSuccess, showErrorAlert } from "@/utils/responseHandler";

import { Button } from "@/components/ui/Button/Button";
import { Menu } from "@/components/ui/Menu";
import {
  renderBadgeCell,
  renderTextCell,
  renderTextSubtextCell,
} from "@/components/table/tableCells";
import { Badge, Table, TableCell } from "@onengine/component-library";
import type { TableColumn } from "@onengine/component-library";
import {
  Anchor,
  Flex,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { IconDotsVertical, IconMapPin, IconStarFilled } from "@tabler/icons-react";

export const Route = createFileRoute("/_authenticated/service-providers/reactive-hire-requests/")({
  component: ReactiveHireRequestsPage,
  beforeLoad: async (args) => {
    // authGuardWithPermissions resolves this path to the service-providers
    // module, so reps also need that module — the seeded "Reactive Hirer"
    // role grants it (see backend roles.json), keep the two in sync.
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });

    const session = getActiveSessionState();
    if (!isReactiveHireRep(session.userRoles, session.userType)) {
      throw redirect({ to: "/" });
    }
  },
});

const STATUS_FILTER_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Requested", value: "requested" },
  { label: "Contacted", value: "contacted" },
  { label: "Onboarding", value: "onboarding" },
  { label: "Completed", value: "completed" },
  { label: "Abandoned", value: "abandoned" },
];

const STATUS_BADGES: Record<ReactiveHireRequestStatus, { label: string; color: string }> = {
  requested: { label: "Requested", color: "info" },
  contacted: { label: "Contacted", color: "indigo" },
  onboarding: { label: "Onboarding", color: "cyan" },
  completed: { label: "Completed", color: "success" },
  abandoned: { label: "Abandoned", color: "danger" },
};

const TRANSITION_ACTION_LABELS: Record<string, string> = {
  contacted: "Mark Contacted",
  onboarding: "Begin Onboarding",
  completed: "Mark Completed",
  abandoned: "Abandon Request",
};

const VIEW_PROVIDER_ACTION = "__view_provider__";

const getProviderName = (request: IReactiveHireRequest): string => {
  if (request.requestType === "company" && request.companyName) {
    return request.companyName;
  }
  return `${request.contactFirstName} ${request.contactLastName}`.trim();
};

function ReactiveHireRequestsPage() {
  useLeaveWhenInsideEntity();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useBreadcrumb(
    [
      {
        title: "Service Providers",
        href: "/service-providers",
        icon: getRouteIcon(routeIconKeys.reactiveHireRequests, 16),
      },
      {
        title: "Reactive Hire Requests",
        href: "/service-providers/reactive-hire-requests",
      },
    ],
    {},
  );

  // This queue widens on super-admin alone, not on leaving an entity, so it
  // uses the reactive-hire predicate rather than isPlatformView.
  const showEntityColumn = useCrossTenantForSuperAdmin();

  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [candidatesRequest, setCandidatesRequest] = useState<IReactiveHireRequest | null>(null);
  const [abandonRequest, setAbandonRequest] = useState<IReactiveHireRequest | null>(null);
  const [abandonReason, setAbandonReason] = useState("");

  const { data, isLoading, isFetching, isPlaceholderData } = useQuery({
    queryKey: [ReactiveHireQueryKeys.requestsList, page, pageSize, statusFilter],
    queryFn: ({ signal }) =>
      getReactiveHireRequests(
        {
          page,
          limit: pageSize,
          status: statusFilter === "all" ? undefined : (statusFilter as ReactiveHireRequestStatus),
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const { mutate: updateStatus } = useMutation({
    mutationFn: updateReactiveHireRequestStatus,
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: [ReactiveHireQueryKeys.requestsList] });
      handleApiSuccess(
        `Request moved to ${STATUS_BADGES[updated.status]?.label ?? updated.status}.`,
      );
    },
    onError: (error: any) => {
      // The axios interceptor only alerts when a response body exists;
      // cover network drops/timeouts so the click never fails silently.
      if (!error?.response?.data) {
        showErrorAlert("Could not update the request status. Please try again.");
      }
    },
  });

  const handleRowAction = (request: IReactiveHireRequest, action: string) => {
    if (action === VIEW_PROVIDER_ACTION) {
      if (request.serviceProvider) {
        navigate({
          to: "/service-providers/$id",
          params: { id: request.serviceProvider.id },
        });
      }
      return;
    }

    const status = action as Exclude<ReactiveHireRequestStatus, "requested">;
    if (status === "abandoned") {
      setAbandonReason("");
      setAbandonRequest(request);
      return;
    }
    updateStatus(
      { requestId: request.id, status },
      {
        onSuccess: () => {
          // "Begin Onboarding" pushes the SP into the existing onboarding
          // flow: the reps continue from the provider's profile page.
          if (status === "onboarding" && request.serviceProvider) {
            navigate({
              to: "/service-providers/$id",
              params: { id: request.serviceProvider.id },
            });
          }
        },
      },
    );
  };

  const columns = useMemo<TableColumn<IReactiveHireRequest>[]>(
    () => [
      {
        key: "workOrder",
        label: "Work Order",
        render: (row) =>
          renderTextSubtextCell(
            row.workOrder?.workOrderNumber ?? "—",
            row.visit?.visitLabel ?? "—",
          ),
      },
      ...(showEntityColumn
        ? [
            {
              key: "entityName",
              label: "Entity",
              render: (row: IReactiveHireRequest) => renderTextCell(row.entityName || "—"),
            } satisfies TableColumn<IReactiveHireRequest>,
          ]
        : []),
      {
        id: "provider",
        key: "companyName",
        label: "Requested Provider",
        render: (row) =>
          renderTextSubtextCell(
            getProviderName(row),
            row.requestType === "company" ? "Company" : "Independent Technician",
          ),
      },
      {
        id: "contact",
        key: "phone",
        label: "Contact",
        render: (row) => renderTextSubtextCell(row.phone, row.email),
      },
      {
        key: "sourceUrl",
        label: "Found At",
        render: (row) => (
          <TableCell spacing="text" inheritBackground>
            <Anchor
              href={row.sourceUrl}
              target="_blank"
              rel="noreferrer"
              size="sm"
              onClick={(event) => event.stopPropagation()}
            >
              {row.sourceUrl.replace(/^https?:\/\//, "")}
            </Anchor>
          </TableCell>
        ),
      },
      {
        id: "candidates",
        key: "googleCandidates",
        label: "Google Candidates",
        render: (row) => (
          <TableCell spacing="text" inheritBackground>
            {row.googleCandidates.length > 0 ? (
              <Anchor
                component="button"
                type="button"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  setCandidatesRequest(row);
                }}
              >
                {row.googleCandidates.length} candidate
                {row.googleCandidates.length === 1 ? "" : "s"}
              </Anchor>
            ) : (
              <Text size="sm" c="var(--text-weak)">
                None found
              </Text>
            )}
          </TableCell>
        ),
      },
      {
        id: "requested",
        key: "createdAt",
        label: "Requested",
        render: (row) =>
          renderTextSubtextCell(
            formatIsoDateToDisplayDateTime(row.createdAt),
            row.requestedBy ? `by ${row.requestedBy.firstName} ${row.requestedBy.lastName}` : "—",
          ),
      },
      {
        key: "status",
        label: "Status",
        render: (row) => {
          const badge = STATUS_BADGES[row.status];
          return renderBadgeCell(
            <Badge color={(badge?.color ?? "neutral") as never} variant="lowContrast" size="small">
              {badge?.label ?? row.status}
            </Badge>,
          );
        },
      },
      {
        id: "actions",
        key: "id",
        label: "",
        render: (row) => {
          const transitions = REACTIVE_HIRE_STATUS_TRANSITIONS[row.status] ?? [];
          const items = [
            ...transitions.map((status) => ({
              label: TRANSITION_ACTION_LABELS[status] ?? status,
              value: status,
            })),
            ...(row.serviceProvider
              ? [{ label: "View Provider Profile", value: VIEW_PROVIDER_ACTION }]
              : []),
          ];

          if (items.length === 0) {
            return renderTextCell("—");
          }

          return (
            <TableCell spacing="text" inheritBackground>
              <div onClick={(event) => event.stopPropagation()}>
                <Menu
                  items={items}
                  onSelect={(value) => handleRowAction(row, value)}
                  trigger={
                    <Button variant="neutral-secondary" aria-label="Request actions">
                      <IconDotsVertical size={16} />
                    </Button>
                  }
                />
              </div>
            </TableCell>
          );
        },
      },
    ],
    [showEntityColumn],
  );

  const rows = data?.data ?? [];
  const showLoadingOverlay = isLoading || (isFetching && isPlaceholderData);

  return (
    <Flex
      direction="column"
      style={{
        margin: "calc(var(--mantine-spacing-md) * -1)",
        padding: "var(--spacing-medium)",
        backgroundColor: "var(--neutral-500)",
      }}
    >
      <Group
        justify="space-between"
        align="center"
        style={{ marginBottom: "var(--spacing-medium)" }}
      >
        <Title
          order={3}
          style={{
            fontWeight: "var(--font-weight-bold)",
            fontSize: "var(--heading-font-size-h3)",
          }}
        >
          Reactive Hire Requests
        </Title>
        <SegmentedControl
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setPage(1);
          }}
          data={STATUS_FILTER_OPTIONS}
          size="xs"
        />
      </Group>

      <div>
        <Table<IReactiveHireRequest>
          tableName="reactiveHireRequests"
          columns={columns}
          data={rows}
          loading={showLoadingOverlay}
          serverSide
          totalCount={data?.pagination.total ?? 0}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showToolbar={false}
          emptyText="No reactive-hire requests found"
        />
      </div>

      <Modal
        opened={candidatesRequest !== null}
        onClose={() => setCandidatesRequest(null)}
        title={
          <Text fw={700} size="lg">
            Google Candidates{" "}
            {candidatesRequest?.workOrder?.workOrderNumber
              ? `— WO ${candidatesRequest.workOrder.workOrderNumber}`
              : ""}
          </Text>
        }
        size="lg"
        centered
      >
        <Stack gap="var(--spacing-small)">
          <Text size="sm" c="var(--text-weak)">
            Providers found by the automatic Google Places search (visit trades within the
            reactive-hire radius of the worksite).
          </Text>
          {(candidatesRequest?.googleCandidates ?? []).map((candidate) => (
            <Flex
              key={candidate.id}
              justify="space-between"
              align="flex-start"
              gap="var(--spacing-small)"
              style={{
                padding: "var(--spacing-small)",
                border: "1px solid var(--border-default)",
                borderRadius: "var(--radius-medium, 8px)",
              }}
            >
              <Stack gap={2}>
                <Text size="sm" fw={600}>
                  {candidate.fullName}
                </Text>
                {candidate.fullAddress ? (
                  <Group gap={4}>
                    <IconMapPin size={14} />
                    <Text size="xs" c="var(--text-weak)">
                      {candidate.fullAddress}
                    </Text>
                  </Group>
                ) : null}
              </Stack>
              <Stack gap={2} align="flex-end">
                {typeof candidate.rating === "number" ? (
                  <Group gap={4}>
                    <IconStarFilled size={14} color="var(--yellow-500, #f5b50a)" />
                    <Text size="xs">
                      {candidate.rating} ({candidate.totalRatings ?? 0})
                    </Text>
                  </Group>
                ) : null}
                {candidate.distance ? (
                  <Text size="xs" c="var(--text-weak)">
                    {candidate.distance}
                  </Text>
                ) : null}
              </Stack>
            </Flex>
          ))}
        </Stack>
      </Modal>

      <Modal
        opened={abandonRequest !== null}
        onClose={() => setAbandonRequest(null)}
        title={
          <Text fw={700} size="lg">
            Abandon Request
          </Text>
        }
        size="md"
        centered
      >
        <Stack gap="var(--spacing-small)">
          <Text size="sm">
            Abandoning is permanent — the request for{" "}
            <Text component="span" fw={600}>
              {abandonRequest ? getProviderName(abandonRequest) : ""}
            </Text>{" "}
            cannot be reopened.
          </Text>
          <Textarea
            label="Reason (optional)"
            placeholder="Why is this request being abandoned?"
            value={abandonReason}
            onChange={(event) => setAbandonReason(event.currentTarget.value)}
            minRows={2}
            autosize
          />
          <Flex gap="var(--spacing-small)" justify="flex-end">
            <Button variant="secondary" type="button" onClick={() => setAbandonRequest(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={() => {
                if (!abandonRequest) return;
                updateStatus({
                  requestId: abandonRequest.id,
                  status: "abandoned",
                  abandonedReason: abandonReason.trim() || undefined,
                });
                setAbandonRequest(null);
              }}
            >
              Abandon Request
            </Button>
          </Flex>
        </Stack>
      </Modal>
    </Flex>
  );
}
